import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {Button, Col, Form, Modal, Row, Spinner, Tab, Tabs} from "react-bootstrap";
import {SimpleCheckbox} from "Components/Common/SimpleCheckbox";
import {AuthInfo, Datasource, ScheduledIngestion, SchoolStore} from "Stores/superStore/schools";
import {CreatableWrapper, LabelValueOption, SelectWrapper} from "Components/Common/SelectWrapper";
import Auth from "../../../../common/auth/auth";
import * as _ from "lodash";
import {Datasources} from "@/pages/internal/school-management/components/datasources";
import {ScheduledIngestions} from "@/pages/internal/school-management/components/scheduled-ingestions";
import {SchoolFeaturesList} from "@/pages/internal/school-management/components/school-features-list";
import {AppStore} from "Stores/appStore";
import {FeatureGatingStore} from "Stores/featureGating";
import DatePicker from "react-datepicker";
import {Utils} from "@/common/utils";
import CategorizerModel from "@/pages/internal/school-management/components/categorizer-model";
import {useDataSourceTemplates} from "@/hooks/datasource";
import {useSchool, useSchoolBasic, useSchoolsOptions} from "@/hooks/schools";
import {EAIClient, SchoolCreationStatusResponse} from "Services/EAIClient";
import {useAppContext} from "@/contexts/app";

const CREATION_STATUS_POLL_INTERVAL_MS = 5000;


// TODO: Move this to a table in the db
const SUPPORTED_TIMEZONES = [
    "America/Adak",
    "America/Anchorage",
    "America/Chicago",
    "America/Denver",
    "America/Detroit",
    "America/Indiana/Indianapolis",
    "America/Indiana/Knox",
    "America/Los_Angeles",
    "America/New_York",
    "America/Phoenix",
    "America/Puerto_Rico",
    "Pacific/Honolulu",
    "Pacific/Pago_Pago",
].map(str => ({value: str, label: str}));

const CONTRACT_OPTIONS: LabelValueOption[] = [
    "Trial" ,
    "Contract" ,
    "Professional Services",
    "Partnership",
    "Internal",
].map(str => ({value: str, label: str}));

const ENTITY_TYPE_OPTIONS: LabelValueOption[] = [
    "Association",
    "College" ,
    "Internal",
    "Partner",
].map(str => ({value: str, label: str}));

// TODO: Move this to a table in the db
const SCHOOL_TYPE_OPTIONS: LabelValueOption[] =[
    "Athletic Training",
    "Biosciences",
    "Business",
    "Chiropractic",
    "Dental",
    "Education",
    "Internal",
    "Law",
    "Medicine",
    "Nursing",
    "Nutrition",
    "Occupational Therapy",
    "Optometry",
    "Osteopathic",
    "Pharmacy",
    "Physical Therapy",
    "Physician Assistant",
    "Podiatry",
    "Veterinary",
    "Other",
].map(str => ({value: str, label: str}));


interface SchoolFormProps {
    schoolStore?: SchoolStore
    appStore?: AppStore
    featureGatingStore?: FeatureGatingStore
    schoolId?: string
    onClose: () => void
    onSave: () => void
}

interface SchoolFormState {
    subdomain: string | null
    domainWhitelist: string
    deletable: boolean
    displayName: string
    schoolId: string
    maxNumberUsers: number
    timezone: LabelValueOption
    schoolType: LabelValueOption | undefined
    ingestible: boolean
    contractType: LabelValueOption | undefined
    datasources: Datasource[]
    scheduledIngestions: ScheduledIngestion[]
    universityName: string
    entityType: LabelValueOption | undefined
    programCollege: string
    programCollegeId: string
    enfluxCreated: string
    enfluxUpdated: string
    hubspotCompanyId: string
    cohort: string
    quickbooksId: number | null
    contractStartDate: Date | null
    authInfo: Partial<AuthInfo>
    initialLoad: boolean
    isProcessing: boolean
    isRefreshingTickets: boolean
    isUpdatingDatasources: boolean
    isUpdatingScheduledIngestions: boolean
    isSaving: boolean
}

export function SchoolForm(props: SchoolFormProps) {
    const GOOD_SCHOOL_ID_CHARACTERS = /^[a-zA-Z]+[a-zA-Z0-9_]*$/;
    const GOOD_HUBSPOT_COMPANY_ID_CHARACTERS = /^[0-9]+$/;
    const PROHIBITED_SCHOOL_IDS = ["dev", "internal", "test", "stage", "prod", "local", "bi", "default", "pytest"];
    const auth = new Auth();
    const [state, setState] = useState<SchoolFormState>({
        subdomain: null,
        domainWhitelist: "",
        deletable: true,
        displayName: "",
        schoolId: "",
        maxNumberUsers: 12,
        timezone: {value:"America/Chicago", label: "America/Chicago"},
        schoolType: undefined,
        ingestible: false,
        contractType: undefined,
        datasources: [],
        scheduledIngestions: [],
        universityName: "",
        entityType: undefined,
        programCollege: "",
        programCollegeId: "",
        enfluxCreated: "",
        enfluxUpdated: "",
        hubspotCompanyId: "",
        cohort: "",
        quickbooksId: null,
        contractStartDate: null,
        authInfo: {},
        initialLoad: false,
        isProcessing: false,
        isRefreshingTickets: false,
        isUpdatingDatasources: false,
        isUpdatingScheduledIngestions: false,
        isSaving: false,
    });

    const {data: datasourceTemplates} = useDataSourceTemplates();
    const {data: school, mutate: mutateSchool} = useSchool(props.schoolId);
    const {mutate: mutateSchoolsBasic} = useSchoolBasic();
    const {mutate: mutateSchoolsOptions} = useSchoolsOptions();
    const eaiClient = new EAIClient();
    const appContext = useAppContext();

    const [creationStatus, setCreationStatus] = useState<SchoolCreationStatusResponse | null>(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [creationError, setCreationError] = useState("");
    const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const createdSchoolIdRef = useRef<string | null>(null);
    const pendingDatasourcesRef = useRef<Datasource[]>([]);

    const handleSchoolSave = async () => {
        const schoolData = {
            school_id: state.schoolId,
            deletable: state.deletable,
            display_name: state.displayName,
            max_number_users: state.maxNumberUsers,
            timezone: state.timezone.value,
            school_type: state.schoolType ? (state.schoolType! as LabelValueOption).value : null,
            ingestible: state.ingestible,
            contract_type: state.contractType ? (state.contractType! as LabelValueOption).value : null,
            university_name: state.universityName,
            entity_type: state.entityType ? (state.entityType! as LabelValueOption).value : null,
            program_college: state.programCollege,
            program_college_id: state.programCollegeId,
            domain_whitelist: state.domainWhitelist,
            hubspot_company_id: state.hubspotCompanyId,
            cohort: state.cohort,
            quickbooks_id: state.quickbooksId,
            contract_start_date: state.contractStartDate ? state.contractStartDate.toISOString() : null,
            auth_info: state.authInfo
        };

        if (props.schoolId) {
            try {
                setState({ ...state, isSaving: true });
                await eaiClient.updateSchool(props.schoolId, schoolData);
                props.onSave();
            } catch (e) {
                appContext.dispatchError(e);
            } finally {
                setState({ ...state, isSaving: false });
            }
        } else {
            setState((prev) => ({ ...prev, isSaving: true }));
            try {
                const response = await eaiClient.createSchool(schoolData);
                if (response.status === 202) {
                    const body = (response.data as { data?: typeof response.data }).data ?? response.data;
                    const schoolIdFromApi = body?.school_id;
                    if (!schoolIdFromApi) {
                        appContext.dispatchError(new Error("Invalid 202 response: missing school_id"));
                        setState((prev) => ({ ...prev, isSaving: false }));
                        return;
                    }
                    createdSchoolIdRef.current = schoolIdFromApi;
                    pendingDatasourcesRef.current = state.datasources;
                    setCreationStatus({
                        status: "processing",
                        step: "initializing",
                        started_at: "",
                        updated_at: "",
                        error: null,
                        progress_percent: 0,
                    });

                    let resolved = false;

                    const stopPolling = () => {
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current);
                            pollingIntervalRef.current = null;
                        }
                    };

                    const poll = async () => {
                        const schoolId = createdSchoolIdRef.current;
                        if (!schoolId) return;
                        try {
                            const statusRes = await eaiClient.getSchoolCreationStatus(schoolId);
                            setCreationStatus(statusRes);
                            if (statusRes.status === "completed") {
                                resolved = true;
                                stopPolling();
                                await auth.handleTokenRenew();
                                const datasources = pendingDatasourcesRef.current;
                                if (datasources.length > 0) {
                                    await Promise.all(
                                        datasources.map((d: Datasource) =>
                                            eaiClient.updateDatasource(schoolId, d.datasource_name, d)
                                        )
                                    );
                                }
                                mutateSchoolsOptions();
                                mutateSchoolsBasic();
                                setCreationStatus(null);
                                setShowSuccessModal(true);
                            } else if (statusRes.status === "failed") {
                                resolved = true;
                                stopPolling();
                                setCreationError(statusRes.error ?? "School creation failed");
                                setCreationStatus(null);
                                setShowErrorModal(true);
                            }
                        } catch (e) {
                            resolved = true;
                            stopPolling();
                            appContext.dispatchError(e);
                            setCreationStatus(null);
                            setShowErrorModal(true);
                            setCreationError("Failed to check creation status");
                        }
                    };

                    await poll();
                    if (!resolved) {
                        pollingIntervalRef.current = setInterval(poll, CREATION_STATUS_POLL_INTERVAL_MS);
                    }
                    return;
                }
                if (response.status === 200) {
                    await auth.handleTokenRenew();
                    if (state.datasources.length > 0) {
                        await Promise.all(
                            state.datasources.map((datasource: Datasource) =>
                                eaiClient.updateDatasource(schoolData.school_id, datasource.datasource_name, datasource)
                            )
                        );
                    }
                    updateSchoolListOptionsThenClose();
                }
            } catch (e) {
                appContext.dispatchError(e);
            } finally {
                setState((prev) => ({ ...prev, isSaving: false }));
            }
        }
    };

    const updateSchoolListOptionsThenClose = () => {
        mutateSchoolsOptions();
        mutateSchoolsBasic();
        props.onClose();
    };

    const handleCancel = () => {
        props.onClose();
    };

    const handleDatasourceUpdate = async (datasource: Datasource) => {
        if(props.schoolId){
            // Create or update datasource for existing school
            try {
                setState({...state, isUpdatingDatasources: true});
                await eaiClient.updateDatasource(props.schoolId, datasource.datasource_name, datasource);
                await mutateSchool();
            } catch (e) {
                appContext.dispatchError(e);
                setState({...state, isUpdatingDatasources: false});
            }
        } else {
            // Create or update pending datasource in state to hold changes until school creation
            const ind = _.findIndex(state.datasources, (d: Datasource) => d.datasource_name === datasource.datasource_name);
            const datasources: Datasource[] = state.datasources.slice(0);
            if(ind === -1){
                // Add pending datasource
                datasources.push(datasource);
            } else {
                // Update pending datasource
                datasources[ind] = datasource;
            }
            setState({...state, datasources});
        }
    };

    const handleDatasourceDelete = async (datasourceName: string) => {
        if(props.schoolId){
            // Update datasource for existing school
            try {
                setState({...state, isUpdatingDatasources: true});
                await eaiClient.deleteDatasource(props.schoolId, datasourceName);
                await mutateSchool();
            } catch (e) {
                appContext.dispatchError(e);
                setState({...state, isUpdatingDatasources: false});
            }
        } else {
            // Remove pending datasource from state
            const datasources = _.filter(state.datasources, (d: Datasource) => d.datasource_name !== datasourceName);
            setState({...state, datasources});
        }
    };

    const handleScheduledIngestionDelete = async (scheduledIngestion: ScheduledIngestion) => {
        if (props.schoolId) {
            // Update Scheduled Ingestion from existing school
            try {
                setState({...state, isUpdatingScheduledIngestions: true});
                await eaiClient.deleteScheduledIngestion(scheduledIngestion.school_id, scheduledIngestion.id);
                await mutateSchool();
            } catch (e) {
                appContext.dispatchError(e);
            } finally {
                setState({...state, isUpdatingScheduledIngestions: false});
            }
        }
    };

    const handleScheduledIngestionSave = async (ingestDate: Date, comment: string, fullIngestion: boolean) => {
        if (props.schoolId) {
            setState({...state, isUpdatingScheduledIngestions: true});
            try {
                await eaiClient.saveScheduledIngestion(
                    props.schoolId,
                    ingestDate,
                    comment,
                    fullIngestion
                );
                await mutateSchool();
            } catch (e) {
                appContext.dispatchError(e);
            } finally {
                setState({...state, isUpdatingScheduledIngestions: false});
            }
        }
    };

    const handleScheduledIngestionUpdate = async (id: number, ingestDate: Date, comment: string, fullIngestion: boolean) => {
        if (props.schoolId) {
            setState({...state, isUpdatingScheduledIngestions: true});
             try {
                 await eaiClient.updateScheduledIngestion(
                         props.schoolId,
                         id,
                         ingestDate,
                         comment,
                         fullIngestion
                 );
                 await mutateSchool();
             } catch (e) {
                 appContext.dispatchError(e);
             } finally {
                 setState({...state, isUpdatingScheduledIngestions: false});
             }
        }
    };

    const handleDeleteClick = () => {
        appContext.showConfirmModal({
            element: (<div>{ `Are you sure you want to delete school ${ state.schoolId }?` }</div>),
            accept: async () => {
                setState({...state, isProcessing: true});
                try {
                    if (props.schoolId) {
                        // Delete school, refresh token, fetch school list, then close
                        await eaiClient.deleteSchool(props.schoolId);
                        await auth.handleTokenRenew();
                        updateSchoolListOptionsThenClose();
                    }
                } catch (e) {
                    appContext.dispatchError(e);
                } finally {
                    setState({...state, isProcessing: false});
                }
            },
            cancel: () => {},
            showActions: true,
        });
    };

    const handleResetSchoolClick = () => {
        appContext.showConfirmModal({
                element: <div>{ `Are you sure you want to reset ticket for school ${ state.schoolId }?` }</div>,
                accept: async () => {
                    try {
                        setState({...state, isRefreshingTickets: true});
                        await eaiClient.refreshTickets(props.schoolId!);
                    } catch (e) {
                        appContext.dispatchError(e);
                    } finally {
                        setState({...state, isRefreshingTickets: false});
                    }
                    props.schoolStore!.refreshTickets(state.schoolId);
                },
                cancel: () => {},
                showActions: true,
        });
    };

    const handleDeletableChange = (deletable: boolean) => {
        setState({...state, deletable});
    };

    const handleDisplayNameChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setState({...state, displayName: ev.target.value});
    };

    const handleDomainWhitelistChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setState({...state, domainWhitelist: ev.target.value});
    };

    const handleSchoolIdChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const schoolId = ev.target.value;
        if (schoolId.length < 20) {
            setState({...state, schoolId});
        }
    };

    const handleMaxNumberUsersChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setState({...state, maxNumberUsers: parseInt(ev.target.value, 10)});
    };

    const handleCohortChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setState({...state, cohort: ev.target.value});
    };

    const handleSubdomainChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setState({...state, authInfo: {...state.authInfo, subdomain: ev.target.value}});
    };

    const handleConnectionStringChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setState({...state, authInfo: {...state.authInfo, connection_string: ev.target.value}});
    };

    const handleQuickbooksIdChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const newVal = ev.target.value.length > 0 ? Number(ev.target.value) : null;
        setState({...state, quickbooksId: newVal});
    };

    const handleContractStartDateChange = (d: Date | null) => {
        setState({...state, contractStartDate: d});
    };

    const handleTimezoneChange = (tz: LabelValueOption) => {
        setState({...state, timezone: tz});
    };

    const handleSchoolTypeChange = (option?: LabelValueOption) => {
        setState({...state, schoolType: option});
    };

    const handleUniversityNameChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setState({...state, universityName: ev.target.value});
    };

    const handleEntityTypeChange = (option?: LabelValueOption) => {
        setState({...state, entityType: option});
    };

    const handleProgramCollegeIdChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setState({...state, programCollegeId: ev.target.value});
    };

    const handleProgramCollegeChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setState({...state, programCollege: ev.target.value});
    };

    const handleIngestibleChange = (ingestible: boolean) => {
        setState({...state, ingestible});
    };

    const handleContractTypeChange = (contractType?: LabelValueOption) => {
        setState({...state, contractType});
    };

    const handleHubspotCompanyIdChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setState({...state, hubspotCompanyId: ev.target.value});
    };

    const isSchoolIdSane = () => {
        return state.schoolId && state.schoolId.match(GOOD_SCHOOL_ID_CHARACTERS);
    };

    const isSchoolIdProhibited = () => {
        return state.schoolId && PROHIBITED_SCHOOL_IDS.indexOf(state.schoolId) > -1;
    };

    const isSchoolIdValid = () => {
        return (isSchoolIdSane() && !isSchoolIdProhibited());
    };

    const isHubspotCompanyIdSane = () => {
        return !!(state.hubspotCompanyId.match(GOOD_HUBSPOT_COMPANY_ID_CHARACTERS));
    };

    const isHubspotCompanyIdRequired = () => {
        return (!state.schoolType ||
            (state.schoolType && (state.schoolType! as LabelValueOption).value !== "Internal")
        );
    };

    const isHubspotCompanyIdValid = () => {
        // If the company id is required, it must be provided and be all numbers
        if (isHubspotCompanyIdRequired()) {
            return (state.hubspotCompanyId && isHubspotCompanyIdSane());
            // IF the company id is optionally provided, it must be all numbers
        } else if (state.hubspotCompanyId) {
            return isHubspotCompanyIdSane();
        }
        // No company id is fine when not an internal school
        return true;
    };

    const isValid = () => {
        const {
            displayName,
            schoolId,
            maxNumberUsers,
            timezone,
        } = state;
        return (
            !!displayName &&
            !!schoolId &&
            maxNumberUsers > 0 &&
            !!timezone &&
            isHubspotCompanyIdValid()
        );
    };

    const getTimezonePromptText = (val: string) => (`Add timezone ${ val }`);

    const renderDatasources = () => (
        <Datasources
            isExistingSchool={!!props.schoolId}
            datasources={state.datasources}
            onUpdateDatasource={handleDatasourceUpdate}
            onDeleteDatasource={handleDatasourceDelete}
            isSaving={state.isUpdatingDatasources}
        />
    );

    const renderScheduledIngestions = () => (
        <ScheduledIngestions
            scheduledIngestions={school?.scheduled_ingestions || []}
            onDeleteScheduledIngestion={handleScheduledIngestionDelete}
            onSaveScheduledIngestion={handleScheduledIngestionSave}
            onUpdateScheduledIngestion={handleScheduledIngestionUpdate}
            isSaving={state.isUpdatingScheduledIngestions}/>
    );

    const renderFeatures = () => {
        if ( !props.schoolId) {
            return;
        }
        return <SchoolFeaturesList schoolId={props.schoolId}/>;
    };

    const renderCategorizerModel = () => {
        if (!props.schoolId) {
            return;
        }
        return <CategorizerModel schoolId={props.schoolId} />;
    };

    const renderForm = () => (
        <React.Fragment>
            <Row className="margin-top">
                <Col md={1}>
                    <SimpleCheckbox
                        id="deletable-checkbox"
                        label="Deletable"
                        isChecked={ state.deletable }
                        onChange={ handleDeletableChange }
                    />
                </Col>
                <Col md={{span:1, offset: 1}}>
                    <SimpleCheckbox
                        id="ingestible-checkbox"
                        label="Ingestible"
                        isChecked={ state.ingestible }
                        onChange={ handleIngestibleChange }
                    />
                </Col>
            </Row>
            <Row className="form-section-horizontal margin-top">
                <Col>
                    <label>School ID</label>
                    <Form.Control
                        id="school-id-input"
                        type="text"
                        disabled={!!props.schoolId}
                        onChange={ handleSchoolIdChange }
                        value={ state.schoolId }
                        isInvalid={ !isSchoolIdValid() }
                    />
                    { isSchoolIdProhibited()
                        ? <Form.Control.Feedback type="invalid">
                            Please enter a valid school id that is not a reserved keyword
                        </Form.Control.Feedback>
                        : !isSchoolIdSane()
                            ? <Form.Control.Feedback type="invalid">
                                Please enter a valid school id that at starts with an [a-zA-Z] followed by [a-zA-Z0-9_] characters
                            </Form.Control.Feedback>
                            : <Form.Label className="required"/> }
                </Col>
                <Col>
                    <label>Display Name</label>
                    <Form.Control
                        id="display-name-input"
                        type="text"
                        required={ true }
                        onChange={ handleDisplayNameChange }
                        value={ state.displayName }
                    />
                    <Form.Label className="required"/>
                </Col>
                <Col>
                    <label>Timezone</label>
                    <CreatableWrapper
                        id="timezone-input"
                        options={ SUPPORTED_TIMEZONES }
                        onChange={ handleTimezoneChange }
                        value={ state.timezone }
                        formatCreateLabel={ getTimezonePromptText }
                    />
                    <Form.Label className="required"/>
                </Col>
                <Col>
                    <label>Hubspot Company Id</label>
                    <Form.Control
                        id="hubspot-company-id-input"
                        type="text"
                        required={ isHubspotCompanyIdRequired()}
                        onChange={ handleHubspotCompanyIdChange }
                        value={ state.hubspotCompanyId}
                        isInvalid={ !isHubspotCompanyIdValid()}
                    />
                    {
                        isHubspotCompanyIdRequired() && !state.hubspotCompanyId ?
                            <Form.Label className="required" /> : state.hubspotCompanyId && !isHubspotCompanyIdValid() ?
                                <Form.Control.Feedback type="invalid">
                                    Please provide a hubspot company id if the school is not an internal school.  The hubspot company id should be numbers.
                                </Form.Control.Feedback> : <Form.Label className="required" />

                    }
                </Col>
            </Row>

            {props.schoolId && (
                <Row className="mt-1">
                    <Form.Group as={Col}>
                        <Form.Label>SSO</Form.Label>
                        <Form.Control value={state.authInfo.is_sso ? "SSO is enabled" : "SSO is disabled"} disabled={true} />
                    </Form.Group>
                    <Form.Group as={Col}>
                        <Form.Label>SSO Connection String</Form.Label>
                        <Form.Control disabled={true} type="text" placeholder="No Connection String Configured" value={state.authInfo.connection_string ?? ""} onChange={handleConnectionStringChange} />
                    </Form.Group>
                    <Form.Group as={Col}>
                        <Form.Label>Subdomain</Form.Label>
                        <Form.Control type="text" placeholder="Insert Subdomain" value={state.authInfo.subdomain ?? ""} onChange={handleSubdomainChange} />
                    </Form.Group>
                </Row>
            )}
            <Row className="margin-bottom">
                <Form.Label className={"col-form-label col-sm-2"}>
                    Domain Whitelist:
                </Form.Label>
                <Form.Control
                    id="domain-whitelist-input"
                    type="text"
                    onChange={ handleDomainWhitelistChange }
                    value={ state.domainWhitelist }
                />
            </Row>
            <Row className="form-section-horizontal">
                <Col md={2}>
                    <label>Max Number of Users</label>
                    <Form.Control
                        id="max-users-input"
                        className="no-icon"
                        type="number"
                        onChange={ handleMaxNumberUsersChange }
                        value={ state.maxNumberUsers }
                        isInvalid={ state.maxNumberUsers < 0 }
                    />
                    { state.maxNumberUsers < 0
                        ? <Form.Control.Feedback type="invalid">
                            Please enter a valid non-negative number
                        </Form.Control.Feedback>
                        : <Form.Label className="required"/> }

                </Col>
                <Col>
                    <label>School Type</label>
                    <SelectWrapper
                        name="school-type-select"
                        options={SCHOOL_TYPE_OPTIONS}
                        onChange={ handleSchoolTypeChange }
                        value={ state.schoolType }
                        isClearable={true}
                    />
                </Col>
                <Col>
                    <label>Contract Type</label>
                    <SelectWrapper
                        name="contract-type-select"
                        options={CONTRACT_OPTIONS}
                        onChange={handleContractTypeChange}
                        value={state.contractType}
                        isClearable={true}
                    />
                </Col>
            </Row>
            <Row className="form-section-horizontal">
                <Col>
                    <label>University Name</label>
                    <Form.Control
                        id="university-name-input"
                        type="text"
                        onChange={ handleUniversityNameChange }
                        value={ state.universityName }
                    />
                </Col>
                <Col md={3}>
                    <label>Entity Type</label>
                    <SelectWrapper
                        name="entity-type-select"
                        options={ENTITY_TYPE_OPTIONS}
                        onChange={ handleEntityTypeChange }
                        value={state.entityType}
                        isClearable={true}
                    />
                </Col>
                <Col md={2}>
                    <label>Created</label>
                    <Form.Control
                        id="enflux-created-display"
                        type="text"
                        disabled={true}
                        value = { state.enfluxCreated }
                    />
                </Col>
            </Row>
            <Row className="form-section-horizontal">
                <Col md={2}>
                    <label>Program College ID</label>
                    <Form.Control
                        id="program-college-id-input"
                        type="text"
                        onChange={ handleProgramCollegeIdChange }
                        value={ state.programCollegeId }
                    />
                </Col>
                <Col>
                    <label>Program College</label>
                    <Form.Control
                        id="program_college-input"
                        type="text"
                        onChange={ handleProgramCollegeChange }
                        value={ state.programCollege }
                    />
                </Col>
                <Col md={2}>
                    <label>Updated</label>
                    <Form.Control
                        id="enflux-updated-display"
                        type="text"
                        disabled={true}
                        value = {state.enfluxUpdated}
                    />
                </Col>
            </Row>
            <Row className="mt-1">
                <Form.Group as={Col}>
                    <Form.Label>Cohort</Form.Label>
                    <Form.Control type="text" placeholder="Insert cohort" value={state.cohort} onChange={handleCohortChange} />
                </Form.Group>
                <Form.Group as={Col}>
                    <Form.Label>Quickbooks ID</Form.Label>
                    <Form.Control type="number" placeholder="Insert Quickbooks ID" value={state.quickbooksId || ""} onChange={handleQuickbooksIdChange} />
                </Form.Group>
                <Form.Group as={Col}>
                    <Form.Label>Contract start date</Form.Label>
                    <DatePicker
                        placeholderText="Select a date..."
                        selected={state.contractStartDate}
                        className="form-control"
                        onChange={handleContractStartDateChange} />
                </Form.Group>
            </Row>


            {!!props.schoolId
                ? <Row className="button-bar form-buttons">
                    <Col className="text-left">
                        <Button
                            variant="danger"
                            size="lg"
                            id="school-form-delete"
                            onClick={handleDeleteClick}
                            disabled={state.isProcessing || !state.deletable}>
                            Delete
                        </Button>
                        {Utils.RESET_SCHOOLS.includes(state.schoolId) && (
                            <Button
                                variant="danger"
                                size="lg"
                                disabled={state.isRefreshingTickets}
                                onClick={handleResetSchoolClick}>Reset Tickets</Button>
                        )}
                    </Col>
                    <Col className="text-end">
                        {state.isProcessing
                            ? <Spinner animation="border" />
                            : <React.Fragment>
                                <Button
                                    variant="secondary"
                                    size="lg"
                                    id="school-form-cancel"
                                    onClick={handleCancel}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    size="lg"
                                    id="school-form-save"
                                    disabled={!isValid() || state.isSaving}
                                    onClick={handleSchoolSave}>
                                    Save
                                </Button>
                            </React.Fragment>}
                    </Col>
                </Row>
                : null}
        </React.Fragment>
    );

    useEffect(() => {
        if (!datasourceTemplates || !school) {
            if (!props.schoolId && datasourceTemplates) {
                setState(prevState => ({...prevState, initialLoad: true}));
            }
            return;
        }
        const {
            subdomain,
            domain_whitelist: domainWhitelist,
            deletable,
            display_name:displayName,
            school_id:schoolId,
            max_number_users:maxNumberUsers,
            timezone,
            school_type:schoolType,
            ingestible,
            contract_type:contractType,
            datasources,
            scheduled_ingestions,
            university_name: universityName,
            entity_type: entityType,
            program_college: programCollege,
            program_college_id: programCollegeId,
            enflux_created: enfluxCreated,
            enflux_updated: enfluxUpdated,
            hubspot_company_id: hubspotCompanyId,
            cohort,
            quickbooks_id,
            contract_start_date,
            auth_info
        } = school;
        setState({
            subdomain,
            domainWhitelist,
            deletable,
            displayName,
            schoolId,
            maxNumberUsers,
            timezone: _.find(SUPPORTED_TIMEZONES, obj => obj.value === timezone) || SUPPORTED_TIMEZONES[0],
            schoolType: schoolType ? _.find(SCHOOL_TYPE_OPTIONS, obj => obj.value === schoolType) : undefined,
            ingestible: ingestible,
            contractType: contractType ? _.find(CONTRACT_OPTIONS, obj => obj.value === contractType) : undefined,
            datasources: _.intersectionWith(datasources, datasourceTemplates, (d1, d2) => d1.datasource_name === d2.datasource_name),
            scheduledIngestions: scheduled_ingestions,
            universityName: universityName ? universityName : "",
            entityType: entityType ? _.find(ENTITY_TYPE_OPTIONS, obj => obj.value === entityType) : undefined,
            programCollege: programCollege ? programCollege : "",
            programCollegeId: programCollegeId ? programCollegeId : "",
            enfluxCreated: !!enfluxCreated ? new Date(enfluxCreated).toLocaleString() : "",
            enfluxUpdated: !!enfluxUpdated ? new Date(enfluxUpdated).toLocaleString() : "",
            hubspotCompanyId: hubspotCompanyId ? hubspotCompanyId : "",
            cohort: cohort || "",
            quickbooksId: quickbooks_id,
            contractStartDate: contract_start_date ? Utils.parseISOLocalDate(contract_start_date) : null,
            authInfo: auth_info,
            initialLoad: true,
            isProcessing: false,
            isRefreshingTickets: false,
            isUpdatingDatasources: false,
            isUpdatingScheduledIngestions: false,
            isSaving: false,
        });
    }, [datasourceTemplates, school]);

    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, []);

    const isPolling = creationStatus?.status === "processing";

    return (
        <div className="table-subcomponent">
            <Tabs defaultActiveKey={1} id="ticketTabs">
                <Tab eventKey={1} title="School Details">
                    {state.initialLoad ? renderForm() : <div className="text-center py-3"><Spinner animation="border" variant="success" /></div>}
                </Tab>
                <Tab eventKey={2} title="School Datasources">
                    {renderDatasources()}
                </Tab>
                <Tab eventKey={3} title="Scheduled Ingestions">
                    {renderScheduledIngestions()}
                </Tab>
                <Tab eventKey={4} title="Features">
                    {renderFeatures()}
                </Tab>
                {props.schoolId && (
                    <Tab eventKey={5} title="Categorizer Model">
                        {renderCategorizerModel()}
                    </Tab>
                )}
            </Tabs>
            {!!props.schoolId
                ? null
                : <Row className="button-bar form-buttons">
                    <Col className="text-end">
                        {state.isProcessing || state.isUpdatingDatasources || state.isSaving || isPolling
                            ? <div className="d-flex align-items-center gap-2 justify-content-end">
                                {isPolling && creationStatus && (
                                    <span className="text-muted">
                                        Creating school... Step: {creationStatus.step} ({creationStatus.progress_percent}% complete)
                                    </span>
                                )}
                                <Spinner animation="border"/>
                            </div>
                            : <React.Fragment>
                                <Button
                                    variant="secondary"
                                    size="lg"
                                    id="school-form-cancel"
                                    disabled={isPolling}
                                    onClick={handleCancel}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    size="lg"
                                    id="school-form-save"
                                    disabled={!isValid() || state.isSaving || isPolling}
                                    onClick={handleSchoolSave}>
                                    Create
                                </Button>
                            </React.Fragment>
                        }
                    </Col>
                </Row>
            }

            <Modal show={showSuccessModal} onHide={() => {}} backdrop="static" keyboard={false} centered>
                <Modal.Header>
                    <Modal.Title>School created successfully!</Modal.Title>
                </Modal.Header>
                <Modal.Body>School creation completed. You can now return to the schools list.</Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => { setShowSuccessModal(false); props.onSave(); }}>
                        Back to list
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showErrorModal} onHide={() => {}} backdrop="static" keyboard={false} centered>
                <Modal.Header>
                    <Modal.Title>School creation failed</Modal.Title>
                </Modal.Header>
                <Modal.Body>{creationError}</Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => { setShowErrorModal(false); setCreationError(""); props.onClose(); }}>
                        Back to list
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
